import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Star, Trash2 } from "lucide-react";
import { http, imgSrc, formatApiError } from "@/lib/api";

export const ImageUploader = ({ images, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const added = [];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await http.post("/upload", fd);
        added.push(data.path);
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    onChange([...images, ...added]);
    if (added.length) toast.success(`${added.length} image${added.length > 1 ? "s" : ""} uploaded`);
    setBusy(false);
    if (input.current) input.current.value = "";
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const cover = (i) => onChange([images[i], ...images.filter((_, idx) => idx !== i)]);

  return (
    <div data-testid="image-uploader">
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {images.map((src, i) => (
          <div key={src + i} className="relative group aspect-square bg-gw-card overflow-hidden">
            <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="lab absolute top-1 left-1 bg-gw-ground text-white px-1.5 py-0.5 text-[10px]">Cover</span>}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
              {i !== 0 && (
                <button type="button" onClick={() => cover(i)} aria-label="Set as cover" data-testid={`image-cover-${i}`} className="p-2 bg-white text-gw-ground"><Star size={14} /></button>
              )}
              <button type="button" onClick={() => remove(i)} aria-label="Remove image" data-testid={`image-remove-${i}`} className="p-2 bg-white text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="image-upload-button"
          className="aspect-square border border-dashed border-gw-ground/40 flex flex-col items-center justify-center gap-2 text-gw-ground transition-colors duration-200 hover:bg-gw-tint disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={20} /> : <ImagePlus size={20} />}
          <span className="lab text-[10px]">{busy ? "Uploading" : "Add photos"}</span>
        </button>
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden
        onChange={(e) => upload(e.target.files)} data-testid="image-file-input" />
    </div>
  );
};
